import {
    Box,
    SxProps,
    Theme,
    Typography,
} from "@mui/material";

import {
    SearchablePopoverSelect,
} from "../SearchablePopoverSelect";

import {
    COUNTRIES,
    ICountry,
} from "../../constants/countries";

interface CountrySelectorProps {
    value?: ICountry | string | null;

    onChange: (country: ICountry) => void;

    disabled?: boolean;

    error?: boolean;

    helperText?: React.ReactNode;

    fullWidth?: boolean;

    sx?: SxProps<Theme>;
}

export const CountrySelector = ({
    value,
    onChange,
    disabled,
    error,
    helperText,
    fullWidth,
    sx,
}: CountrySelectorProps) => {
    // Support both ICountry object and string (dialCode like "+91" or code like "IN")
    const resolvedValue: ICountry | null | undefined = (() => {
        if (!value) return null;
        if (typeof value === "object") return value;
        // Find by dialCode or code
        return (
            COUNTRIES.find((c) => c.dialCode === value) ||
            COUNTRIES.find((c) => c.code === value) ||
            null
        );
    })();

    return (
        <SearchablePopoverSelect<ICountry>
            items={COUNTRIES}
            value={resolvedValue}
            onChange={onChange}

            getKey={(country) => country.code}

            getSearchText={(country) =>
                [
                    country.name,
                    country.code,
                    country.iso3,
                    country.dialCode,
                ].join(" ")
            }

            renderValue={(country) =>
                country ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        {/* <Typography component="span">
                            {country.flag}
                        </Typography> */}

                        <Typography component="span">
                            {country.name}
                        </Typography>
                    </Box>
                ) : null
            }

            renderItem={(country) => (
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 20,
                        }}
                    >
                        {country.flag}
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            flex: 1,
                        }}
                    >
                        {country.name}
                    </Typography>

                    {/* <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        {country.dialCode}
                    </Typography> */}
                </Box>
            )}

            placeholder="Country"
            searchPlaceholder="Search country"

            disabled={disabled}

            error={error}

            helperText={helperText}

            fullWidth={fullWidth}

            sx={sx}
        />
    );
};


interface CountryCodeSelectorProps {
    value?: string;
    onChange: (country: ICountry) => void;
    disabled?: boolean;
    error?: string;
    label?: string;
    variant?: "outlined" | "standard" | "filled";
    sx?: SxProps<Theme>;
}

/**
 * CountryCodeSelector — takes dialCode string as value (e.g. "+91")
 * and returns full ICountry object on change.
 */
export const CountryCodeSelector = ({
    value,
    onChange,
    disabled,
    error,
    label,
    variant = "outlined",
    sx,
}: CountryCodeSelectorProps) => {
    const resolvedValue: ICountry | null = value
        ? COUNTRIES.find((c) => c.code === value) || null
        : null;

    return (
        <SearchablePopoverSelect<ICountry>
            items={COUNTRIES}
            value={resolvedValue}
            onChange={onChange}
            getKey={(country) => country.code}
            fullWidth
            sx={{
                ".MuiOutlinedInput-root": {
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    fontSize: "0.8rem",
                    color: "rgb(38, 38, 38)",
                    width: "100%",
                },
                ...sx
            }}
            variant={variant}
            getSearchText={(country) =>
                [country.name, country.code, country.iso3, country.dialCode].join(" ")
            }
            renderValue={(country) =>
                country ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography component="span" sx={{ color: "#454545" }}>{country.flag}</Typography>
                        <Typography component="span" sx={{ color: "#454545" }}>{country.dialCode}</Typography>
                    </Box>
                ) : null
            }
            renderItem={(country) => (
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <Typography component="span" sx={{ fontSize: 20, color: "rgb(38, 38, 38)", }}>
                        {country.flag}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1, color: "rgb(38, 38, 38)", }}>
                        {country.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgb(38, 38, 38)", }}>
                        {country.dialCode}
                    </Typography>
                </Box>
            )}
            placeholder={label || "Code"}
            label={label}
            searchPlaceholder="Search country"
            disabled={disabled}
            error={!!error}
            helperText={error}
        />
    );
};
